import React, { Component } from "react";
import Helmet from "react-helmet";
import Navbar from "../Navbar/Navbar";
import {
  FormControl,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  ListItemText,
  Chip,
  FormControlLabel,
  Switch
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import ShareIcon from "@material-ui/icons/Share";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CropPortraitIcon from "@material-ui/icons/CropPortrait";
import FlareIcon from "@material-ui/icons/Flare";
import Footer from "../Footer/Footer";
import LazyImage from "../LazyImage/LazyImage";

import constants from "../Constants/constants";
import { fetchCardNames } from './cardNameCache';
import { buildEndpoint, performCardSearch, ELEMENT_NAMES, RARITY_OPTIONS, CARD_TYPES, TURN_COUNTS, ABILITY_COSTS, COMBAT_ATTRIBUTES, CARD_SETS } from './searchUtils';
import { getDualSideName } from '../DeckBuilder/deckUtils';
import "./Search.css";

export default class Search extends Component {
  constructor(props) {
    super(props);
    this.state = {
      search: "",
      characteristic: "",
      cardEffect: "",
      rarityQuery: [],
      cardSetQuery: [],
      elementsQuery: [],
      typeQuery: [],
      turnCountQuery: [],
      combatQuery: [],
      resultsLoading: false,
      results: [],
      hasSearched: false,
      selectedCard: null,
      dualElementsOnly: false,
      hideKickstarter: true,
      darkMode: false,
      abilityCostQuery: [],
      abilityCostElementQuery: [],
      showBackToTop: false,
      copiedToast: null,
      touchStartX: null,
      sortBy: 'name',
      sortDirection: 'asc',
      showRarityEffects: true,
      suggestions: [],
      showSuggestions: false,
      cardUsageStats: null
    };
    this.searchTimeout = null;
    this.toastTimeout = null;
    this.rarityOrder = ['Legendary Rare', 'Secret Rare', 'Super Rare', 'Rare', 'Uncommon', 'Common', 'Promo', 'Starter'];
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    document.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('scroll', this.handleScroll);

    // Load saved theme preference and set background accordingly
    const savedTheme = localStorage.getItem('searchPageTheme');
    const isDark = savedTheme === null ? false : savedTheme === 'dark';

    this.setBodyBackground(isDark);

    if (savedTheme !== null) {
      this.setState({ darkMode: isDark });
    }

    // Load saved rarity effects preference
    const savedRarityEffects = localStorage.getItem('showRarityEffects');
    if (savedRarityEffects !== null) {
      this.setState({ showRarityEffects: savedRarityEffects === 'true' });
    }

    // Pre-fetch card names for fuzzy search (non-blocking, falls back to static JSON)
    fetchCardNames();

    // Fetch card usage stats (non-blocking)
    fetch(`${constants.DOMAIN}api/v1/guest/deck/stats`)
      .then(res => res.json())
      .then(data => { if (this._mounted) this.setState({ cardUsageStats: data }); })
      .catch(() => {});

    // Parse URL parameters to restore shared search
    this.loadFromUrlParams();
  }

  loadFromUrlParams = () => {
    const params = new URLSearchParams(window.location.search);

    const newState = {};
    let hasParams = false;

    if (params.get('search')) {
      newState.search = params.get('search');
      hasParams = true;
    }
    if (params.get('characteristic')) {
      newState.characteristic = params.get('characteristic');
      hasParams = true;
    }
    if (params.get('cardEffect')) {
      newState.cardEffect = params.get('cardEffect');
      hasParams = true;
    }
    if (params.get('element')) {
      newState.elementsQuery = params.get('element').split(',');
      hasParams = true;
    }
    if (params.get('rarity')) {
      newState.rarityQuery = params.get('rarity').split(',');
      hasParams = true;
    }
    if (params.get('type')) {
      newState.typeQuery = params.get('type').split(',');
      hasParams = true;
    }
    if (params.get('turn')) {
      newState.turnCountQuery = params.get('turn').split(',');
      hasParams = true;
    }
    if (params.get('combat')) {
      newState.combatQuery = params.get('combat').split(',');
      hasParams = true;
    }
    if (params.get('set')) {
      newState.cardSetQuery = params.get('set').split(',');
      hasParams = true;
    }
    if (params.get('cost')) {
      newState.abilityCostQuery = params.get('cost').split(',');
      hasParams = true;
    }
    if (params.get('dual') === '1') {
      newState.dualElementsOnly = true;
      hasParams = true;
    }
    if (params.get('kickstarter') === '1') {
      newState.hideKickstarter = false;
      hasParams = true;
    }
    if (params.get('costElement')) {
      newState.abilityCostElementQuery = params.get('costElement').split(',');
      hasParams = true;
    }
    if (params.get('sort')) {
      const [sortBy, sortDirection] = params.get('sort').split('-');
      if (['name', 'rarity', 'turnCount', 'cardNumber'].includes(sortBy) &&
          ['asc', 'desc'].includes(sortDirection)) {
        newState.sortBy = sortBy;
        newState.sortDirection = sortDirection;
      }
    }

    if (hasParams) {
      this.setState(newState);
    }
  }

  setBodyBackground(isDark) {
    // Match the page background color to prevent flash
    const bgColor = isDark ? '#0d0d1a' : '#9D0208';
    document.documentElement.style.backgroundColor = bgColor;
    document.body.style.backgroundColor = bgColor;
  }

  componentDidUpdate(prevProps, prevState) {
    // Update body background when dark mode changes
    if (prevState.darkMode !== this.state.darkMode) {
      this.setBodyBackground(this.state.darkMode);
    }

    // Check if any filter changed
    const filterKeys = [
      'search', 'characteristic', 'cardEffect', 'rarityQuery',
      'cardSetQuery', 'elementsQuery', 'typeQuery', 'turnCountQuery', 'combatQuery',
      'dualElementsOnly', 'hideKickstarter', 'abilityCostQuery', 'abilityCostElementQuery'
    ];

    const filtersChanged = filterKeys.some(key => {
      if (Array.isArray(this.state[key])) {
        return JSON.stringify(prevState[key]) !== JSON.stringify(this.state[key]);
      }
      return prevState[key] !== this.state[key];
    });

    if (filtersChanged) {
      // Clear URL params if user changes filters after loading from shared link
      this.clearUrlParams();

      // Debounce the search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.performSearch();
      }, 400);
    }
  }

  clearUrlParams = () => {
    // Only clear if there are URL params present
    if (window.location.search) {
      window.history.replaceState({}, '', '/search');
    }
  }

  componentWillUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    this._mounted = false;
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('scroll', this.handleScroll);
    // Reset html and body styles when leaving (including scroll lock from modal)
    document.documentElement.style.backgroundColor = '';
    document.documentElement.style.overflow = '';
    document.body.style.backgroundColor = '';
    document.body.style.overflow = '';
  }

  handleKeyDown = (e) => {
    if (!this.state.selectedCard) return;

    if (e.key === 'Escape') {
      this.closeModal();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.goToPrevCard();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.goToNextCard();
    }
  }

  getCurrentCardIndex = () => {
    const { selectedCard, results } = this.state;
    if (!selectedCard || results.length === 0) return -1;
    const sortedResults = this.sortResults(results);
    return sortedResults.findIndex(card => card._id === selectedCard._id);
  }

  goToPrevCard = () => {
    const { results } = this.state;
    const sortedResults = this.sortResults(results);
    const currentIndex = this.getCurrentCardIndex();
    if (currentIndex > 0) {
      this.setState({ selectedCard: sortedResults[currentIndex - 1] });
    }
  }

  goToNextCard = () => {
    const { results } = this.state;
    const sortedResults = this.sortResults(results);
    const currentIndex = this.getCurrentCardIndex();
    if (currentIndex < sortedResults.length - 1) {
      this.setState({ selectedCard: sortedResults[currentIndex + 1] });
    }
  }

  handleTouchStart = (e) => {
    // Ignore multi-touch (pinch-to-zoom)
    if (e.touches.length > 1) {
      this.setState({ touchStartX: null });
      return;
    }
    this.setState({ touchStartX: e.touches[0].clientX });
  }

  handleTouchEnd = (e) => {
    const { touchStartX } = this.state;
    if (touchStartX === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swiped left - go to next card
        this.goToNextCard();
      } else {
        // Swiped right - go to previous card
        this.goToPrevCard();
      }
    }

    this.setState({ touchStartX: null });
  }

  handleScroll = () => {
    const scrollY = window.scrollY;
    if (scrollY > 400 && !this.state.showBackToTop) {
      this.setState({ showBackToTop: true });
    } else if (scrollY <= 400 && this.state.showBackToTop) {
      this.setState({ showBackToTop: false });
    }

  }

  scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  openModal = (card) => {
    this.setState({ selectedCard: card });
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  closeModal = () => {
    this.setState({ selectedCard: null });
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }

  handleFlipCard = async (otherName) => {
    // Search current results for the other side
    const found = this.state.results.find(c => (c.cardName || c.name) === otherName);
    if (found) {
      this.setState({ selectedCard: found });
      return;
    }
    // Fetch from API
    try {
      const res = await fetch(`${constants.DOMAIN}api/v1/guest/post?search=${encodeURIComponent(otherName)}`);
      const data = await res.json();
      const cards = data.posts || [];
      const exact = cards.find(c => (c.cardName || c.name) === otherName);
      if (exact) {
        this.setState({ selectedCard: exact });
      } else if (cards.length > 0) {
        this.setState({ selectedCard: cards[0] });
      }
    } catch (err) {
      console.error("Failed to fetch dual-sided card:", err);
    }
  }

  showCopiedToast = (message) => {
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    this.setState({ copiedToast: message });
    this.toastTimeout = setTimeout(() => {
      this.setState({ copiedToast: null });
    }, 2500);
  }

  generateSearchUrl = () => {
    const { search, characteristic, cardEffect, rarityQuery, cardSetQuery,
      elementsQuery, typeQuery, turnCountQuery, combatQuery, abilityCostQuery,
      abilityCostElementQuery, dualElementsOnly, hideKickstarter, sortBy, sortDirection } = this.state;

    const params = new URLSearchParams();

    if (search.trim()) params.set('search', search.trim());
    if (characteristic.trim()) params.set('characteristic', characteristic.trim());
    if (cardEffect.trim()) params.set('cardEffect', cardEffect.trim());
    if (elementsQuery.length > 0) params.set('element', elementsQuery.join(','));
    if (rarityQuery.length > 0) params.set('rarity', rarityQuery.join(','));
    if (typeQuery.length > 0) params.set('type', typeQuery.join(','));
    if (turnCountQuery.length > 0) params.set('turn', turnCountQuery.join(','));
    if (combatQuery.length > 0) params.set('combat', combatQuery.join(','));
    if (cardSetQuery.length > 0) params.set('set', cardSetQuery.join(','));
    if (abilityCostQuery.length > 0) params.set('cost', abilityCostQuery.join(','));
    if (abilityCostElementQuery.length > 0) params.set('costElement', abilityCostElementQuery.join(','));
    if (dualElementsOnly) params.set('dual', '1');
    if (!hideKickstarter) params.set('kickstarter', '1');
    if (sortBy !== 'name' || sortDirection !== 'asc') {
      params.set('sort', `${sortBy}-${sortDirection}`);
    }

    const baseUrl = window.location.origin + '/search';
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  shareSearch = async () => {
    if (!this.hasFilters()) return;

    const url = this.generateSearchUrl();

    try {
      await navigator.clipboard.writeText(url);
      this.showCopiedToast('Search link copied!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.showCopiedToast('Search link copied!');
    }
  }

  shareCard = async (card) => {
    const url = `${window.location.origin}/card/${card._id}`;

    try {
      await navigator.clipboard.writeText(url);
      this.showCopiedToast('Card link copied!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.showCopiedToast('Card link copied!');
    }
  }

  sortResults = (results) => {
    const { sortBy, sortDirection } = this.state;
    const sorted = [...results];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'rarity':
          const aRarityIndex = this.rarityOrder.indexOf(a.rarity);
          const bRarityIndex = this.rarityOrder.indexOf(b.rarity);
          comparison = aRarityIndex - bRarityIndex;
          break;
        case 'turnCount':
          // Cards without turn count (Ability, Field) go to the end
          const aHasTurn = a.cardType === 'Character' || a.cardType === 'Strategy';
          const bHasTurn = b.cardType === 'Character' || b.cardType === 'Strategy';
          if (aHasTurn && !bHasTurn) {
            comparison = -1;
          } else if (!aHasTurn && bHasTurn) {
            comparison = 1;
          } else if (!aHasTurn && !bHasTurn) {
            comparison = 0;
          } else {
            const aTurn = parseInt(a.turnCount, 10) || 0;
            const bTurn = parseInt(b.turnCount, 10) || 0;
            comparison = aTurn - bTurn;
          }
          break;
        case 'cardNumber':
          const aNum = a.cardNumber || '';
          const bNum = b.cardNumber || '';
          comparison = aNum.localeCompare(bNum, undefined, { numeric: true });
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }

  handleSortChange = (e) => {
    const value = e.target.value;
    const [sortBy, sortDirection] = value.split('-');
    this.setState({ sortBy, sortDirection });
  }

  hasFilters() {
    const { search, characteristic, cardEffect, rarityQuery, cardSetQuery,
      elementsQuery, typeQuery, turnCountQuery, combatQuery, abilityCostQuery,
      abilityCostElementQuery, dualElementsOnly, hideKickstarter } = this.state;
    return search.trim() || characteristic.trim() || cardEffect.trim() ||
      rarityQuery.length > 0 || cardSetQuery.length > 0 ||
      elementsQuery.length > 0 || typeQuery.length > 0 ||
      turnCountQuery.length > 0 || combatQuery.length > 0 ||
      abilityCostQuery.length > 0 || abilityCostElementQuery.length > 0 ||
      dualElementsOnly || !hideKickstarter;
  }

  buildEndpointForType(cardTypes, includeTurnCount = false, searchOverride) {
    return buildEndpoint(this.state, cardTypes, includeTurnCount, searchOverride);
  }

  // Simple ability cost count - just sum numbers + count elements
  // Used for rough filtering only (data format is inconsistent for precise calculation)
  getAbilityCostCount(card) {
    if (!card.abilityCost) return 0;

    let items = [];
    if (Array.isArray(card.abilityCost)) {
      items = card.abilityCost;
    } else if (typeof card.abilityCost === 'string') {
      items = card.abilityCost.split(',').map(s => s.trim()).filter(s => s);
    }

    let total = 0;
    for (const item of items) {
      const lowerItem = item.toLowerCase();
      if (lowerItem === 'and' || lowerItem === 'or') continue;

      const num = parseInt(item, 10);
      if (!isNaN(num)) {
        total += num;
      } else {
        // Count each character as 1 element
        for (const char of lowerItem) {
          if (/[a-z]/.test(char)) {
            total += 1;
          }
        }
      }
    }

    return total;
  }

  // Extract element types from ability cost (returns lowercase element names)
  getAbilityCostElements(card) {
    const elements = new Set();

    // First, try to extract from abilityCost field
    if (card.abilityCost) {
      let items = [];
      if (Array.isArray(card.abilityCost)) {
        items = card.abilityCost;
      } else if (typeof card.abilityCost === 'string') {
        items = card.abilityCost.split(',').map(s => s.trim()).filter(s => s);
      }

      const ignoreWords = ['or', 'and'];
      const elementMap = {
        // Single letters (S = Sea/Water, P = Plasma, F = Fire, T = Terra, A = Air, N = Necro)
        's': 'water', 'p': 'plasma', 'f': 'fire', 't': 'terra', 'a': 'air', 'n': 'necro',
        'w': 'water', // W might also be used for water
        // Full names
        'plasma': 'plasma', 'fire': 'fire', 'water': 'water',
        'terra': 'terra', 'air': 'air', 'necro': 'necro',
        // Color variations that might be used
        'blue': 'water', 'red': 'fire', 'green': 'terra',
        'yellow': 'air', 'purple': 'plasma', 'black': 'necro'
      };

      for (const item of items) {
        const lowerItem = item.toLowerCase();
        if (ignoreWords.includes(lowerItem)) continue;
        if (!isNaN(parseInt(item, 10))) continue; // Skip numbers

        const mapped = elementMap[lowerItem];
        if (mapped) {
          elements.add(mapped);
        }
      }
    }

    return Array.from(elements);
  }

  async performSearch() {
    if (!this.hasFilters()) {
      this.setState({ results: [], hasSearched: false });
      return;
    }

    this.setState({ resultsLoading: true, hasSearched: true });

    try {
      const result = await performCardSearch(this.state, {
        getAbilityCostCount: this.getAbilityCostCount.bind(this),
        getAbilityCostElements: this.getAbilityCostElements.bind(this),
      });

      if (result.suggestions.length > 0) {
        this.setState({ results: [], resultsLoading: false, suggestions: result.suggestions, showSuggestions: true });
      } else {
        this.setState({ results: result.posts, resultsLoading: false, suggestions: [], showSuggestions: false });
      }
    } catch (err) {
      console.error(err);
      this.setState({ resultsLoading: false });
    }
  }

  resetFilters = () => {
    this.setState({
      search: "",
      characteristic: "",
      cardEffect: "",
      rarityQuery: [],
      cardSetQuery: [],
      elementsQuery: [],
      typeQuery: [],
      turnCountQuery: [],
      combatQuery: [],
      abilityCostQuery: [],
      abilityCostElementQuery: [],
      results: [],
      hasSearched: false,
      dualElementsOnly: false,
      hideKickstarter: true,
      suggestions: [],
      showSuggestions: false
    });
  }

  handleSuggestionClick = (cardName) => {
    this.setState({ search: cardName, suggestions: [], showSuggestions: false });
  }

  handleMultiSelectChange = (stateKey) => (event) => {
    const newValue = event.target.value;

    if (stateKey === 'typeQuery') {
      const updates = { [stateKey]: newValue };

      const hasAbility = newValue.includes('Ability');
      const hasTurnCountTypes = newValue.includes('Character') || newValue.includes('Strategy');
      const onlyAbility = hasAbility && !hasTurnCountTypes && !newValue.includes('Field');

      // Clear ability cost filters if Ability is deselected
      if (!hasAbility) {
        if (this.state.abilityCostQuery.length > 0) updates.abilityCostQuery = [];
        if (this.state.abilityCostElementQuery.length > 0) updates.abilityCostElementQuery = [];
      }

      // Clear turn count if Character and Strategy are both deselected
      if (!hasTurnCountTypes && newValue.length > 0 && this.state.turnCountQuery.length > 0) {
        updates.turnCountQuery = [];
      }

      // Reset sort if current sort becomes invalid
      const currentSort = this.state.sortBy;
      if (onlyAbility && currentSort === 'turnCount') {
        updates.sortBy = 'name';
        updates.sortDirection = 'asc';
      }

      this.setState(updates);
    } else {
      this.setState({ [stateKey]: newValue });
    }
  }

  goToCard = (card) => {
    this.openModal(card);
  }

  goToFullPage = (card) => {
    localStorage.setItem("card", JSON.stringify(card));
    this.props.navigate(`/card/${card._id}`);
  }

  getRarityClass = (rarity) => {
    if (!this.state.showRarityEffects) return '';

    switch (rarity) {
      case 'Secret Rare':
        return 'rarity-secret';
      case 'Legendary Rare':
        return 'rarity-legendary';
      case 'Super Rare':
        return 'rarity-super';
      default:
        return '';
    }
  }

  toggleRarityEffects = () => {
    const newValue = !this.state.showRarityEffects;
    this.setState({ showRarityEffects: newValue });
    localStorage.setItem('showRarityEffects', newValue.toString());
  }

  handleCardMouseMove = (e, rarity) => {
    if (rarity !== 'Secret Rare') return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    card.style.setProperty('--shine-x', `${x}%`);
    card.style.setProperty('--shine-y', `${y}%`);
  }

  handleCardMouseLeave = (e, rarity) => {
    if (rarity !== 'Secret Rare') return;

    const card = e.currentTarget;
    card.style.setProperty('--shine-x', '50%');
    card.style.setProperty('--shine-y', '50%');
  }

  cleanCardText = (text) => {
    if (!text) return '';

    let cleaned = text;

    // Remove keywords from the beginning (loop to catch multiple)
    const startKeywords = ['TRIGGER', 'Rush', 'NECRO', 'PLASMA', 'FIRE', 'WATER', 'TERRA', 'AIR', 'ACTIVE', 'ONGOING', 'PASSIVE', 'ACTIVATE'];
    let foundStart = true;
    while (foundStart) {
      foundStart = false;
      for (const keyword of startKeywords) {
        const regex = new RegExp(`^${keyword}\\s+`, 'i');
        if (regex.test(cleaned)) {
          cleaned = cleaned.replace(regex, '');
          foundStart = true;
          break;
        }
      }
    }

    // Remove trailing metadata like "TRIGGER Green 1 ('Richie')" or "Transformation icon"
    cleaned = cleaned.replace(/\s*TRIGGER\s+\w+\s*\d*\s*\([^)]*\)\s*.*$/i, '');
    cleaned = cleaned.replace(/\s*Transformation icon\s*$/i, '');

    // Remove trailing characteristics like "Dragoninked | Male" or "Pirate | Female"
    cleaned = cleaned.replace(/\s+[\w-]+\s*\|\s*[\w-]+\s*$/i, '');

    // Remove trailing metadata patterns like "GC 1 AIR 1 FIRE 1 TRIGGER Green 1"
    // Pattern: combinations of KEYWORD + optional number at the end
    const metadataKeywords = '(GC|PLASMA|FIRE|WATER|TERRA|AIR|NECRO|ACTIVE|ONGOING|PASSIVE|ACTIVATE|TRIGGER|Green|Red|Blue|Yellow|Purple|Orange)';
    const metadataPattern = new RegExp(`(\\s+${metadataKeywords}\\s*\\d*)+\\s*$`, 'gi');
    cleaned = cleaned.replace(metadataPattern, '');

    return cleaned.trim();
  }

  getSetPurchaseUrl = (setName) => {
    const setUrls = {
      'Awakened Primordials': 'https://shop.primaltcg.com/products/primal-tcg-1st-ed-awakened-primordials-booster-box',
      'Battlers of Bethesda': 'https://shop.primaltcg.com/products/primal-tcg-battlers-of-bethesda-booster-box',
      'Warden of the Seven Deadly Sins': 'https://shop.primaltcg.com/products/primal-tcg-warden-of-the-seven-sins-booster-box-1st-ed'
    };
    return setUrls[setName] || null;
  }

  renderMultiSelect(id, label, options, stateKey) {
    const selectedValues = this.state[stateKey];
    const hasValue = selectedValues.length > 0;

    return (
      <FormControl variant="outlined" fullWidth className={hasValue ? 'has-value' : ''}>
        <InputLabel
          id={`${id}-label`}
          shrink={hasValue}
        >
          {label}
        </InputLabel>
        <Select
          labelId={`${id}-label`}
          id={id}
          multiple
          value={selectedValues}
          onChange={this.handleMultiSelectChange(stateKey)}
          label={label}
          displayEmpty
          notched={hasValue}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return null;
            }
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </div>
            );
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              }
            }
          }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox checked={selectedValues.indexOf(option) > -1} />
              <ListItemText primary={option} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  renderResults() {
    const { results, resultsLoading, hasSearched } = this.state;

    if (resultsLoading) {
      return (
        <div className="loading-container" role="status" aria-live="polite">
          <CircularProgress size={40} aria-hidden="true" />
          <p>Searching cards...</p>
        </div>
      );
    }

    if (!hasSearched) {
      return (
        <div className="initial-state" role="status">
          <h3>Use the filters above to search for cards</h3>
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div className="no-results" role="status" aria-live="polite">
          <div className="no-results-icon" aria-hidden="true">🔍</div>
          <h3>No cards found</h3>
          <p>Try adjusting your search filters</p>
          {this.state.showSuggestions && this.state.suggestions.length > 0 && (
            <div className="suggestions-container">
              <p className="suggestions-label">Did you mean?</p>
              <div className="suggestions-list">
                {this.state.suggestions.map((name) => (
                  <button
                    key={name}
                    className="suggestion-btn"
                    onClick={() => this.handleSuggestionClick(name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    const sortedResults = this.sortResults(results);

    return (
      <div className="results-grid" role="list" aria-label="Search results">
        {sortedResults.map((card) => (
          <article
            key={card._id}
            className={`result-card ${this.getRarityClass(card.rarity)}`}
            onClick={() => this.goToCard(card)}
            onKeyPress={(e) => e.key === 'Enter' && this.goToCard(card)}
            onMouseMove={(e) => this.handleCardMouseMove(e, card.rarity)}
            onMouseLeave={(e) => this.handleCardMouseLeave(e, card.rarity)}
            role="listitem"
            tabIndex={0}
            aria-label={`${card.name} - ${card.cardType} - ${card.rarity}. Click to view details.`}
          >
            <LazyImage
              src={`https://api.primaltcg.com/${card.image}`}
              alt={`${card.name} card artwork`}
            />
          </article>
        ))}
      </div>
    );
  }

  renderModal() {
    const { selectedCard, darkMode, results } = this.state;
    if (!selectedCard) return null;

    const card = selectedCard;
    const dualSideName = getDualSideName(card);
    const themeClass = darkMode ? 'modal-dark' : 'modal-light';

    const modalStyle = {
      backgroundColor: darkMode ? '#2a2a3a' : '#ffffff'
    };

    // Navigation state
    const currentIndex = this.getCurrentCardIndex();
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < results.length - 1;
    const showNavigation = results.length > 1;

    return (
      <div
        className={`card-modal-overlay ${themeClass}`}
        onClick={this.closeModal}
        onTouchStart={this.handleTouchStart}
        onTouchEnd={this.handleTouchEnd}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-card-title"
      >
        {/* Navigation Arrows */}
        {showNavigation && (
          <>
            <button
              className={`modal-nav-btn modal-nav-prev ${!canGoPrev ? 'disabled' : ''}`}
              onClick={(e) => { e.stopPropagation(); this.goToPrevCard(); }}
              disabled={!canGoPrev}
              aria-label="Previous card"
            >
              <ChevronLeftIcon aria-hidden="true" />
            </button>
            <button
              className={`modal-nav-btn modal-nav-next ${!canGoNext ? 'disabled' : ''}`}
              onClick={(e) => { e.stopPropagation(); this.goToNextCard(); }}
              disabled={!canGoNext}
              aria-label="Next card"
            >
              <ChevronRightIcon aria-hidden="true" />
            </button>
          </>
        )}

        <div className={`card-modal ${themeClass}`} style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <button
            className="modal-close-btn"
            onClick={this.closeModal}
            aria-label="Close card details"
          >
            <CloseIcon aria-hidden="true" />
          </button>

          <div className="modal-content" style={modalStyle}>
            <figure className="modal-image">
              <img src={`${constants.DOMAIN}${card.image}`} alt={`${card.name} - ${card.cardType} card from ${card.cardSet}`} />
              {dualSideName && (
                <button
                  className="flip-card-btn"
                  onClick={() => this.handleFlipCard(dualSideName)}
                  aria-label={`Flip to ${dualSideName}`}
                  title={`Flip to ${dualSideName}`}
                >
                  ↻ Flip
                </button>
              )}
            </figure>

            <div className="modal-footer">
              {this.getSetPurchaseUrl(card.cardSet) ? (
                <a
                  className="modal-set modal-set-link"
                  href={this.getSetPurchaseUrl(card.cardSet)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {card.cardSet} 🛒
                </a>
              ) : (
                <span className="modal-set">{card.cardSet}</span>
              )}
              {card.artist && card.artist.name && (
                <span
                  className="modal-artist"
                  onClick={() => card.artist.link && window.open(card.artist.link)}
                >
                  Art: {card.artist.name}
                </span>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="modal-share-btn"
                onClick={() => this.shareCard(card)}
                aria-label="Share card link"
                title="Share Card"
              >
                <ShareIcon aria-hidden="true" /> Share
              </button>
              <button
                className="modal-view-page-btn"
                onClick={() => this.goToFullPage(card)}
              >
                View Card Page
              </button>
            </div>

            {showNavigation && (
              <span className="modal-pagination">{currentIndex + 1} of {results.length}</span>
            )}

            {this.state.cardUsageStats && this.state.cardUsageStats.totalDecks > 0 && (() => {
              const deckCount = this.state.cardUsageStats.cardUsage[card._id] || 0;
              const rawPct = (deckCount / this.state.cardUsageStats.totalDecks) * 100;
              const pct = Math.round(rawPct);
              const label = deckCount > 0 && pct === 0 ? "< 1" : String(pct);
              const tier = rawPct >= 76 ? "platinum" : rawPct >= 51 ? "gold" : rawPct >= 26 ? "silver" : deckCount > 0 ? "bronze" : "none";
              return (
                <p className={`modal-usage usage-${tier}`}>
                  Used in {label}% of decks
                </p>
              );
            })()}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      search,
      characteristic,
      cardEffect,
      results,
      hasSearched,
      resultsLoading
    } = this.state;

    const elements = ELEMENT_NAMES;
    const rarity = RARITY_OPTIONS;
    const cardType = CARD_TYPES;
    const turnCount = TURN_COUNTS;
    const abilityCosts = ABILITY_COSTS;
    const skills = COMBAT_ATTRIBUTES;
    const cardSets = CARD_SETS;
    const showAbilityCost = this.state.typeQuery.includes("Ability");
    // Show Turn Count only when Character or Strategy is selected (or no type filter)
    const typeQuery = this.state.typeQuery;
    const showTurnCount = typeQuery.length === 0 ||
      typeQuery.includes("Character") ||
      typeQuery.includes("Strategy");

    const hasFilters = this.hasFilters();

    const { darkMode } = this.state;

    return (
      <div className={`Search ${darkMode ? 'dark-mode' : 'light-mode'}`} role="application">
        <Helmet>
          <title>Card Search | Primal Trading Card Game - Find TCG Cards by Element, Rarity & More</title>
          <meta name="description" content="Search and filter Primal TCG cards by element, rarity, card type, turn count, and more. Browse the complete card database for deck building and collection." />
          <meta name="keywords" content="Primal TCG, trading card game, card search, TCG cards, deck building, collectible cards" />
          <meta property="og:title" content="Primal TCG Card Search - Browse All Cards" />
          <meta property="og:description" content="Search and filter Primal TCG cards by element, rarity, card type, turn count, and more. Browse the complete card database." />
          <meta property="og:image" content="https://pbs.twimg.com/media/GJ38D7mWwAAMXsa?format=jpg&name=medium" />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Primal TCG Card Search" />
          <meta name="twitter:description" content="Search and filter Primal TCG cards by element, rarity, card type, and more." />
          <link rel="canonical" href="https://primaltcg.com/search" />
        </Helmet>
        <a className="skip-link visually-hidden" href="#search-results">Skip to search results</a>
        <Navbar />
        <main className="row bkg-red" role="main">
          <div className="col-xs-12 col-sm-10 col-md-10 col-lg-8 col-xl-8 offset-sm-1 offset-md-1 offset-lg-2 offset-xl-2">
            {/* Filter Panel */}
            <section className="filter-panel" aria-labelledby="search-heading">
              <h1 id="search-heading">Primal TCG Card Search</h1>
              <div className="filter-header">
                <h2 className="filter-header-label" id="filters-label">Filters</h2>
                <div className="filter-header-actions" role="group" aria-label="Filter actions">
                  <button
                    className="theme-toggle-btn"
                    onClick={() => {
                      const newMode = !darkMode;
                      this.setState({ darkMode: newMode });
                      localStorage.setItem('searchPageTheme', newMode ? 'dark' : 'light');
                    }}
                    aria-label={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                    title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                  >
                    {darkMode ? <Brightness7Icon aria-hidden="true" /> : <Brightness4Icon aria-hidden="true" />}
                  </button>
                  <button
                    className={`rarity-effects-btn ${this.state.showRarityEffects ? 'active' : ''}`}
                    onClick={this.toggleRarityEffects}
                    aria-label={this.state.showRarityEffects ? "Disable Rarity Shine" : "Enable Rarity Shine"}
                    title={this.state.showRarityEffects ? "Disable Rarity Shine" : "Enable Rarity Shine"}
                  >
                    <CropPortraitIcon aria-hidden="true" className="card-icon" />
                    {this.state.showRarityEffects && <FlareIcon aria-hidden="true" className="sparkle-icon" />}
                  </button>
                  <button
                    className="share-search-btn"
                    onClick={this.shareSearch}
                    disabled={!hasFilters}
                    aria-label="Share search link"
                    title="Share Search"
                  >
                    <ShareIcon aria-hidden="true" />
                  </button>
                  <button
                    className="clear-filters-btn"
                    onClick={this.resetFilters}
                    disabled={!hasFilters}
                    aria-label="Clear all filters"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Search Input */}
              <div className="search-row" role="search">
                <FormControl variant="outlined" fullWidth className="search-input-field">
                  <TextField
                    variant="outlined"
                    id="search"
                    label="Search by card name..."
                    value={search}
                    autoComplete="off"
                    onChange={(e) => this.setState({ search: e.target.value })}
                    inputProps={{
                      'aria-label': 'Search cards by name',
                      'aria-describedby': 'search-help'
                    }}
                  />
                  <span id="search-help" className="visually-hidden">
                    Type a card name to search. Results update automatically.
                  </span>
                </FormControl>
              </div>

              {/* Filter Dropdowns Grid */}
              <div className="filter-grid" role="group" aria-labelledby="filters-label">
                {this.renderMultiSelect("elements", "Element", elements, "elementsQuery")}
                {this.renderMultiSelect("rarity", "Rarity", rarity, "rarityQuery")}
                {this.renderMultiSelect("cardType", "Card Type", cardType, "typeQuery")}
                {showTurnCount && this.renderMultiSelect("turnCount", "Turn Count", turnCount, "turnCountQuery")}
                {this.renderMultiSelect("combat", "Combat Attribute", skills, "combatQuery")}
                {this.renderMultiSelect("cardSet", "Card Set", cardSets, "cardSetQuery")}
                {showAbilityCost && this.renderMultiSelect("abilityCost", "Ability Cost", abilityCosts, "abilityCostQuery")}
                {showAbilityCost && this.renderMultiSelect("abilityCostElement", "Ability Cost Element", elements, "abilityCostElementQuery")}
              </div>

              {/* Filter Options */}
              <div className="filter-options">
                <FormControlLabel
                  control={
                    <Switch
                      checked={this.state.dualElementsOnly}
                      onChange={(e) => this.setState({ dualElementsOnly: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Dual Elements Only (match ALL selected)"
                  className="filter-toggle"
                  disabled={this.state.elementsQuery.length < 2}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={this.state.hideKickstarter}
                      onChange={(e) => this.setState({ hideKickstarter: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Hide Kickstarter Cards"
                  className="filter-toggle"
                />
              </div>

              {/* Text Filters */}
              <div className="text-filters-section" role="group" aria-labelledby="text-filters-label">
                <p className="text-filters-label" id="text-filters-label">Text Filters</p>
                <div className="text-filters-row">
                  <FormControl variant="outlined" fullWidth>
                    <TextField
                      variant="outlined"
                      id="characteristic"
                      label="Search by characteristics..."
                      value={characteristic}
                      autoComplete="off"
                      onChange={(e) => this.setState({ characteristic: e.target.value })}
                    />
                  </FormControl>
                  <FormControl variant="outlined" fullWidth>
                    <TextField
                      variant="outlined"
                      id="cardEffect"
                      label="Search by card effect..."
                      value={cardEffect}
                      autoComplete="off"
                      onChange={(e) => this.setState({ cardEffect: e.target.value })}
                    />
                  </FormControl>
                </div>
              </div>
            </section>

            {/* Results Section */}
            <section
              id="search-results"
              className="results-section"
              aria-labelledby="results-heading"
              aria-live="polite"
              aria-busy={resultsLoading}
            >
              <div className="results-header">
                <div className="results-header-left">
                  <h2 id="results-heading" className="results-title">Results</h2>
                  {hasSearched && !resultsLoading && (
                    <span className="results-count" aria-live="polite">
                      {results.length} card{results.length !== 1 ? 's' : ''} found
                    </span>
                  )}
                </div>
                {hasSearched && results.length > 1 && !resultsLoading && (
                  <div className="results-sort">
                    <label htmlFor="sort-select" className="sort-label">Sort by:</label>
                    <select
                      id="sort-select"
                      className="sort-select"
                      value={`${this.state.sortBy}-${this.state.sortDirection}`}
                      onChange={this.handleSortChange}
                      aria-label="Sort results"
                    >
                      <option value="name-asc">Name (A-Z)</option>
                      <option value="name-desc">Name (Z-A)</option>
                      <option value="rarity-asc">Rarity (Highest first)</option>
                      <option value="rarity-desc">Rarity (Lowest first)</option>
                      {showTurnCount && <option value="turnCount-asc">Turn Count (Low-High)</option>}
                      {showTurnCount && <option value="turnCount-desc">Turn Count (High-Low)</option>}
                      <option value="cardNumber-asc">Card Number (Ascending)</option>
                      <option value="cardNumber-desc">Card Number (Descending)</option>
                    </select>
                  </div>
                )}
              </div>
              {this.renderResults()}
            </section>
          </div>
        </main>
        <Footer />
        {this.renderModal()}
        {this.state.showBackToTop && (
          <button
            className="back-to-top-btn"
            onClick={this.scrollToTop}
            title="Back to top"
            aria-label="Scroll back to top of page"
          >
            <KeyboardArrowUpIcon aria-hidden="true" />
          </button>
        )}
        {this.state.copiedToast && (
          <div className="copied-toast" role="status" aria-live="polite">
            {this.state.copiedToast}
          </div>
        )}
      </div>
    );
  }
}
